import { register as apiRegister, login as apiLogin, type AuthResponse, refreshAccessToken } from "./authApi";
import { syncAndroidWebViewDebug } from "./webViewDebug";

/**
 * User data from backend authentication response
 */
export type AuthUser = {
  id: string;
  email: string;
};

/**
 * Authentication session from backend
 * Matches backend response format with JWT tokens
 */
export type AuthSession = {
  token: string;
  refreshToken: string;
  user: AuthUser;
  createdAt: string;
};

// ---------------------------------------------------------------------------
// Auth session is stored in localStorage (not Dexie) so it is readable before
// we know which user's database to open — avoiding a circular dependency.
// ---------------------------------------------------------------------------

const SESSION_LS_KEY = "macrotracker:auth-session";

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isAuthSession = (raw: unknown): raw is AuthSession => {
  if (!isObjectRecord(raw)) {
    return false;
  }

  const user = raw.user;
  return (
    typeof raw.token === "string" &&
    raw.token.length > 0 &&
    typeof raw.refreshToken === "string" &&
    raw.refreshToken.length > 0 &&
    typeof raw.createdAt === "string" &&
    isObjectRecord(user) &&
    typeof user.id === "string" &&
    user.id.length > 0 &&
    typeof user.email === "string" &&
    user.email.length > 0
  );
};

const readSessionFromStorage = (): AuthSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_LS_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as unknown;
    if (!isAuthSession(session)) {
      removeSessionFromStorage();
      return null;
    }
    return session;
  } catch {
    removeSessionFromStorage();
    return null;
  }
};

const writeSessionToStorage = (session: AuthSession): void => {
  localStorage.setItem(SESSION_LS_KEY, JSON.stringify(session));
};

const removeSessionFromStorage = (): void => {
  localStorage.removeItem(SESSION_LS_KEY);
};

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const syncNativeWebViewDebugForSession = async (session: AuthSession | null): Promise<void> => {
  await syncAndroidWebViewDebug(session ? isUserDeveloper(session) : false);
};

/**
 * Set the authentication session in storage
 *
 * @param session - Session data from backend auth response
 */
const setSession = async (
  session: Pick<AuthSession, "token" | "refreshToken" | "user">,
): Promise<void> => {
  const authSession: AuthSession = {
    token: session.token,
    refreshToken: session.refreshToken,
    user: session.user,
    createdAt: new Date().toISOString(),
  };
  writeSessionToStorage(authSession);
  await syncNativeWebViewDebugForSession(authSession);
};

/**
 * Create a new user account with the backend
 *
 * @param email - User email
 * @param password - User password (min 8 characters)
 * @returns Auth session with tokens and user data
 * @throws Error if registration fails or account exists
 */
export const createAccount = async (
  email: string,
  password: string,
): Promise<AuthSession> => {
  const normalizedEmail = normalizeEmail(email);

  // Call backend register endpoint
  const authResponse = await apiRegister({
    email: normalizedEmail,
    password,
  });

  // Store tokens in local storage
  await setSession(authResponse);

  return {
    token: authResponse.token,
    refreshToken: authResponse.refreshToken,
    user: authResponse.user,
    createdAt: new Date().toISOString(),
  };
};

/**
 * Validate user credentials and create a session
 *
 * @param email - User email
 * @param password - User password
 * @returns Auth session with tokens and user data
 * @throws Error if authentication fails
 */
export const validateLogin = async (
  email: string,
  password: string,
): Promise<AuthSession | null> => {
  const normalizedEmail = normalizeEmail(email);

  try {
    // Call backend login endpoint
    const authResponse = await apiLogin({
      email: normalizedEmail,
      password,
    });

    // Store tokens in local storage
    await setSession(authResponse);

    return {
      token: authResponse.token,
      refreshToken: authResponse.refreshToken,
      user: authResponse.user,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    // Return null on authentication failure (matches previous behavior)
    if (error instanceof Error && error.message.includes("401")) {
      return null;
    }
    throw error;
  }
};

/**
 * Get the current authentication session from storage.
 */
export const getSession = async (): Promise<AuthSession | null> => {
  const session = readSessionFromStorage();
  await syncNativeWebViewDebugForSession(session);
  return session;
};

/**
 * Clear current authentication session from storage
 */
export const clearSession = async (): Promise<void> => {
  removeSessionFromStorage();
  await syncNativeWebViewDebugForSession(null);
};

/**
 * Initiate Google OAuth sign-in/sign-up flow
 * Redirects user to backend OAuth endpoint, which then redirects to Google
 * The OAuth callback will return to /auth/callback which handles the session
 *
 * @throws Error if OAuth initiation fails
 */
export const initiateGoogleOAuth = async (): Promise<void> => {
  const { getGoogleOAuthUrl } = await import("./authApi");
  const oauthUrl = await getGoogleOAuthUrl();

  // Redirect browser to the OAuth endpoint
  window.location.href = oauthUrl;
};

/**
 * Sign in with Google using the native Android SDK (via @capgo/capacitor-social-login).
 * Obtains a Google ID token natively and exchanges it with the backend for JWT tokens.
 *
 * @throws Error if sign-in or token exchange fails
 */
export const initiateNativeGoogleSignIn = async (): Promise<AuthSession> => {
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  const { exchangeGoogleIdToken } = await import("./authApi");

  await SocialLogin.initialize({
    google: {
      webClientId: import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID,
    },
  });

  const result = await SocialLogin.login({
    provider: "google",
    options: {},
  });

  if (result.provider !== "google" || result.result.responseType !== "online") {
    throw new Error("Unexpected Google sign-in response");
  }

  const { idToken } = result.result;
  if (!idToken) {
    throw new Error("Google sign-in did not return an ID token");
  }

  const authResponse = await exchangeGoogleIdToken(idToken);
  await setSession(authResponse);

  return {
    token: authResponse.token,
    refreshToken: authResponse.refreshToken,
    user: authResponse.user,
    createdAt: new Date().toISOString(),
  };
};


/**
 * JWT token payload interface for type safety
 */
interface JwtPayload {
  exp: number; // Expiration time as Unix timestamp
  iat: number; // Issued at time as Unix timestamp
  sub: string; // Subject (user ID)
  email: string; // User email
  aud: string; // Audience
  iss: string; // Issuer
  nbf: number; // Not before time
  isAdmin?: boolean; // Admin claim from backend
  developer?: boolean; // Developer claim from backend
  ai_describe?: boolean; // AI describe entitlement claim
  ai_photos?: boolean; // AI photo entitlement claim
  real_time_sync?: boolean; // Real-time sync entitlement claim
  intervals_icu_sync?: boolean; // Intervals.icu sync entitlement claim
}

export type RawJwtClaims = Record<string, unknown>;

export type ParsedJwtClaims = {
  claims: RawJwtClaims;
  normalized: {
    isAdmin: boolean;
    developer: boolean;
    ai_describe: boolean;
    ai_photos: boolean;
    real_time_sync: boolean;
    intervals_icu_sync: boolean;
  };
};

const parseBooleanClaim = (
  value: unknown,
  defaultValue: boolean,
): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return defaultValue;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") {
      return true;
    }
    if (normalized === "false" || normalized === "0") {
      return false;
    }
  }

  return defaultValue;
};

const decodeJwtSegment = (segment: string): string => {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4);
  return atob(padded);
};

export const decodeJwtClaims = (token: string): RawJwtClaims | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const decodedPayload = decodeJwtSegment(parts[1]);
    const parsedPayload = JSON.parse(decodedPayload) as unknown;
    if (!parsedPayload || typeof parsedPayload !== 'object' || Array.isArray(parsedPayload)) {
      return null;
    }

    return parsedPayload as RawJwtClaims;
  } catch {
    return null;
  }
};

export const parseJwtClaimsForDisplay = (token: string): ParsedJwtClaims | null => {
  const claims = decodeJwtClaims(token);
  if (!claims) {
    return null;
  }

  return {
    claims,
    normalized: {
      isAdmin: parseBooleanClaim(claims.isAdmin, false),
      developer: parseBooleanClaim(claims.developer, false),
      ai_describe: parseBooleanClaim(claims.ai_describe, true),
      ai_photos: parseBooleanClaim(claims.ai_photos, false),
      real_time_sync: parseBooleanClaim(claims.real_time_sync, false),
      intervals_icu_sync: parseBooleanClaim(claims.intervals_icu_sync, false),
    },
  };
};

/**
 * Safely parse JWT token payload without verification
 * 
 * @param token - JWT token string to parse
 * @returns Parsed payload or null if token is malformed
 */
const parseJwtPayload = (token: string): JwtPayload | null => {
  const parsedPayload = decodeJwtClaims(token);
  if (!parsedPayload) {
    return null;
  }

  if (
    'exp' in parsedPayload &&
    typeof parsedPayload.exp === 'number' &&
    'iat' in parsedPayload &&
    typeof parsedPayload.iat === 'number'
  ) {
    return parsedPayload as unknown as JwtPayload;
  }

  return null;
};

export const ensureValidSession = async (): Promise<AuthSession | null> => {
  const currentSession = await getSession();
  if (!currentSession) {
    return null;
  }

  const { token: currentSessionTTL, refresh: refreshTTL } = sessionTTL(currentSession);

  if (currentSessionTTL > 300) {
    return currentSession;
  }

  if (currentSessionTTL < 300 && refreshTTL > 5) {
    try {
      const refreshedTokens = await refreshAccessToken(currentSession.refreshToken);
      const nextSession: AuthSession = {
        token: refreshedTokens.token,
        refreshToken: refreshedTokens.refreshToken,
        user: currentSession.user,
        createdAt: new Date().toISOString(),
      };
      await setSession(nextSession);
      return nextSession;
    } catch (e) {
      console.log(`error while refreshing access token : ${e}`);
      return null;
    }
  }

  return null;
};

export const sessionTTL = (session: AuthSession): { token: number, refresh: number } => {
  return {
    token: jwtTTL(session.token),
    refresh: jwtTTL(session.refreshToken)
  };
};

/**
 * Calculate the time-to-live (TTL) for a session in seconds
 * 
 * Parses the JWT token to extract the expiration time and returns
 * the number of seconds until the session expires. Returns 0 if the
 * token is expired, invalid, or malformed.
 * 
 * @param session - Authentication session containing JWT token
 * @returns Number of seconds until token expires (0 if expired/invalid)
 */
export const jwtTTL = (token: string): number => {
  const jwt = parseJwtPayload(token);
  if (!jwt) {
    return 0;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = jwt.exp;

  // Calculate TTL, but never return negative values
  const ttl = expirationTime - currentTime;
  return ttl > 0 ? ttl : 0;
};

/**
 * Check if the current user has admin privileges
 * 
 * @param session - Authentication session containing JWT token
 * @returns True if user has admin claim, false otherwise
 */
export const isUserAdmin = (session: AuthSession): boolean => {
  const payload = parseJwtPayload(session.token);
  return parseBooleanClaim(payload?.isAdmin, false);
};

/**
 * Check if the current session belongs to an admin user
 * Helper that works with getSession() result
 * 
 * @param session - Session from getSession() or null
 * @returns True if session exists and user is admin
 */
export const isCurrentUserAdmin = async (): Promise<boolean> => {
  const session = await getSession();
  return session ? isUserAdmin(session) : false;
};

/**
 * Check if the current user has developer privileges
 *
 * @param session - Authentication session containing JWT token
 * @returns True if user has developer claim, false otherwise
 */
export const isUserDeveloper = (session: AuthSession): boolean => {
  const payload = parseJwtPayload(session.token);
  return parseBooleanClaim(payload?.developer, false);
};

/**
 * Check if the current session belongs to a developer user
 *
 * @returns True if session exists and user is developer
 */
export const isCurrentUserDeveloper = async (): Promise<boolean> => {
  const session = await getSession();
  return session ? isUserDeveloper(session) : false;
};

/**
 * Check if the current user has the AI describe entitlement.
 *
 * @param session - Authentication session containing JWT token
 * @returns True if user has AI describe claim, false otherwise
 */
export const isUserAiDescribeEnabled = (session: AuthSession): boolean => {
  const payload = parseJwtPayload(session.token);
  return parseBooleanClaim(payload?.ai_describe, true);
};

/**
 * Check if the current session belongs to a user with AI describe enabled.
 *
 * @returns True if session exists and user has the AI describe claim
 */
export const isCurrentUserAiDescribeEnabled = async (): Promise<boolean> => {
  const session = await getSession();
  return session ? isUserAiDescribeEnabled(session) : false;
};

/**
 * Check if the current user has the AI photo-processing entitlement.
 *
 * @param session - Authentication session containing JWT token
 * @returns True if user has AI photo claim, false otherwise
 */
export const isUserAiPhotosEnabled = (session: AuthSession): boolean => {
  const payload = parseJwtPayload(session.token);
  return parseBooleanClaim(payload?.ai_photos, false);
};

/**
 * Check if the current session belongs to a user with AI photo processing enabled.
 *
 * @returns True if session exists and user has the AI photo claim
 */
export const isCurrentUserAiPhotosEnabled = async (): Promise<boolean> => {
  const session = await getSession();
  return session ? isUserAiPhotosEnabled(session) : false;
};

export { setSession };
